import Observer from "./Scrolly/Components/Observer";

interface Options {
    debug              ?:boolean,
    itemSelector        :string,
    triggerSelector     :string,
    contentSelector     :string,
    activeClass         :string,
    expandSingle       ?:boolean,
    expandFirstAtStart ?:boolean,
    duration           ?:number
}

declare global {
    interface HTMLElement {
        _accordionTrigger: HTMLElement|null;
        _accordionContent: HTMLElement|null;
    }
}

/**
 * Accordion.
 *
 * @author Felix Kiesewetter <info@felixkiesewetter.de>
 * @lastModified 2026-09-03 TypeScript improvements, event handlers will not be attached when content is missing.
 * @package fk_js/accordion
 * @version 2.1.0
 */
class Accordion
{

    private errors   :string[] = [];
    private items    :NodeListOf<HTMLElement>|null = null;
    private node     :HTMLElement;
    private settings :Options = {
        debug: false,
        itemSelector: ':scope [class*="item"]',
        triggerSelector: ':scope [class*="trigger"]',
        contentSelector: ':scope [class*="content"]',
        activeClass: '-expanded',
        expandSingle: false,
        expandFirstAtStart: true,
        duration: 350
    };

    constructor(node :HTMLElement, options :Options)
    {
        this.node = node;
        this.settings = this.mergeSettings(options);
        if (!this.node) {
            this.errors.push(`Accordion expects first parameter to be a DOM element but variable node is of type ${typeof node}`);
        }
        else {
            this.node.classList.add('accordion');
            this.items = this.node.querySelectorAll<HTMLElement>(this.settings.itemSelector);
            if (!this.items) {
                this.errors.push(`Accordion has no items that could be selected via selector ${this.settings.itemSelector}`);
            }
            else {
                this.init();
            }
        }
        this.debug();
    }

    /**
     * Initialize the accordions.
     */
    private init() :void
    {
        this.node.style.overflowY = 'hidden';
        let itemKey :number = 0;
        const accordionKey :number = 1; // TODO: multiple accordions on the same page should have different ids
        this.items?.forEach((item :HTMLElement) :void => {
            itemKey += 1;
            item.classList.add('accordion__item');
            item._accordionTrigger = item.querySelector(this.settings.triggerSelector);
            item._accordionContent = item.querySelector(this.settings.contentSelector);
            if (!item._accordionContent) {
                this.errors.push(`Accordion item has no content that could be selected via selector ${this.settings.contentSelector}`);
                return;
            }
            else {
                item._accordionContent.classList.add('accordion__item__content');
                item._accordionContent.setAttribute('role', 'region');
                item._accordionContent.setAttribute('aria-labelledby', `accordion-${accordionKey}-item-${itemKey}`);
                item._accordionContent.setAttribute('aria-expanded', 'false');
                item._accordionContent.style.transition = `height ${this.settings.duration}ms ease-in-out`;
                item._accordionContent.style.overflow = 'hidden';
                item._accordionContent.style.height = '0';
                item._accordionContent.setAttribute('hidden', '');
                if (!item._accordionTrigger) {
                    this.errors.push(`Accordion item has no trigger that could be selected via selector ${this.settings.triggerSelector}`);
                }
                else {
                    item._accordionTrigger.classList.add('accordion__item__trigger');
                    item._accordionTrigger.id = `accordion-${accordionKey}-item-${itemKey}`;
                    item._accordionTrigger.setAttribute('role', 'button');
                    item._accordionTrigger.addEventListener('click', () :void => {
                        this.toggle(item);
                    });
                }
            }
        })
        if (this.errors.length === 0 && this.settings.expandFirstAtStart) {
            this.items?.item(0) && this.toggle(this.items?.item(0));
        }
    }

    /**
     * Toggle an accordion item.
     */
    toggle(item :HTMLElement) :void
    {
        if (this.settings.expandSingle === true) {
            this.items?.forEach((i :HTMLElement) :void => {
                if (i !== item && i.classList.contains(this.settings.activeClass)) {
                    this.collapse(i);
                }
            });
        }
        if (!item.classList.contains(this.settings.activeClass)) {
            this.expand(item);
        }
        else if (item.classList.contains(this.settings.activeClass)) {
            this.collapse(item);
        }
    }

    /**
     * Expand an accordion item.
     */
    expand(item :HTMLElement) :void
    {
        if (!item._accordionContent) {
            return;
        }
        item._accordionContent.setAttribute('aria-expanded', 'true');
        item._accordionContent.removeAttribute('hidden');
        item._accordionContent.style.height = this.getHeight(item._accordionContent);
        item.classList.add(this.settings.activeClass);
        setTimeout(() :void => {
            item._accordionContent ? item._accordionContent.style.height = '' : null;
        }, this.settings.duration);
        window.dispatchEvent(new Event('resize'));
    }

    /**
     * Collapse an accordion item.
     */
    collapse(item :HTMLElement) :void
    {
        if (!item._accordionContent) {
            return;
        }
        item._accordionContent.setAttribute('aria-expanded', 'false');
        item._accordionContent.style.height = item._accordionContent.scrollHeight + 'px';
        setTimeout(() => {
            item._accordionContent ? item._accordionContent.style.height = '0' : null;
        }, 1);
        setTimeout(() => {
            item.classList.remove(this.settings.activeClass);
            item._accordionContent ? item._accordionContent.setAttribute('hidden', '') : null;
        }, this.settings.duration);
    }

    /**
     * Get the height of the content when it's not collapsed.
     */
    getHeight(content :HTMLElement) :string
    {
        content.style.display = 'block';
        const height :string = content.scrollHeight + 'px';
        content.style.display = '';
        return height;
    }

    /**
     * Merge incoming options with default settings and settings from dataset.
     */
    private mergeSettings(options :Options) :Options
    {
        let dataSettings :{ expandFirstAtStart ?:boolean, expandSingle ?:boolean} = {};
        this.node.getAttribute('data-expand-first') === '0' ? dataSettings.expandFirstAtStart = false : null;
        this.node.getAttribute('data-expand-single') === '1' ? dataSettings.expandSingle = true : null;
        return {...this.settings, ...dataSettings, ...options};
    }

    /**
     * Debug function.
     */
    private debug() :void
    {
        if (this.settings.debug) {
            console.log({
                settings: this.settings,
                items: this.items,
                errors: this.errors
            });
        }
    }

    /**
     * Static initialization helper auto-initializes all accordions on a page.
     */
    static init(selector :string = '.accordion', options :Options) :void
    {
        const accordions :NodeListOf<HTMLElement> = document.querySelectorAll(selector);
        accordions?.forEach((accordion :Node) :void => {
            new Accordion(accordion as HTMLElement, options);
        });
    }

}

export default Accordion;